
import compact from 'lodash/compact'
import map from 'lodash/map'
import React, { FC, useMemo } from 'react'

import { CardList } from '@Components/Card/CardList'
import { PageGridProps } from '@Components/CMS/PageGrid'
import { Page as GraphQLPage, useLatestArticlesAndCollectionsQuery } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'

export const getPageData = (pages: GraphQLPage[], pageTypeFallback: string): PageGridProps['items'] => compact(
  map(
    pages,
    x => ({
      brand: x.brand
        ? {
          id: x.brand.id || '',
          logo: x.brand.logo || undefined,
          title: x.brand.title || '',
        }
        : undefined,
      description: x.openGraph?.description || '',
      id: x.id || '',
      image: x.openGraph?.imageURL || '',
      link: x.url || '',
      pageType: x.openGraph?.type || pageTypeFallback || '',
      title: x.openGraph?.title || '',
    }),
  ),
)

interface LatestArticlesAndCollectionsBlockProps {
  excludeArticleIDs?: string[]
  excludeCollectionIDs?: string[]
  hideArticles?: boolean
  hideCollections?: boolean
}

const MOBILE_LIMIT = 3

export const LatestArticlesAndCollectionsBlock: FC<LatestArticlesAndCollectionsBlockProps> = ({ excludeArticleIDs,
  excludeCollectionIDs,
  hideArticles,
  hideCollections }) => {
  const { t } = useTranslation()
  const { data } = useLatestArticlesAndCollectionsQuery({
    variables: {
      excludeArticleIDs,
      excludeCollectionIDs,
    },
  })
  const articles = useMemo(() => (hideArticles ? [] : getPageData((data?.articles || []) as GraphQLPage[], 'articles')), [hideArticles, data])
  const collections = useMemo(() => (hideCollections ? [] : getPageData((data?.collections || []) as GraphQLPage[], 'collections')), [hideCollections, data])

  if (!data) {
    return null
  }

  return (
    <>
      {!hideCollections && (
        <CardList
          data={collections}
          label={t('header:VIEW_ALL_COLLECTIONS_LABEL')}
          mobileLimit={MOBILE_LIMIT}
          title={t('header:NEW_COLLECTIONS_LABEL')}
          url="collections"
        />
      )}
      {!hideArticles && (
        <CardList
          data={articles}
          label={t('header:VIEW_ALL_ARTICLES_LABEL')}
          mobileLimit={MOBILE_LIMIT}
          title={t('header:NEW_ARTICLES_LABEL')}
          url="articles"
        />
      )}
    </>
  )
}
